<template>
  <div>
    <button class="btn btn-edit" @click="showModal"><img src="@/assets/images/admin/cashpayments/edit.png" alt="Edit" style="width: 25px; height: 25px;" />
</button>

    <!-- Modal -->
    <div class="modal fade" ref="chargeModal" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-xl custom-modal" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Edit {{ store.currentType }}</h5>
            <button type="button" class="close" @click="hideModal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="row d-flex align-items-stretch">
              <!-- Details Card -->
              <div class="col-xl-3 col-12 d-flex">
                <div class="card flex-grow-1">
                  <div class="card-body">
                    <h5 class="section-title">Details</h5>
                    <div class="row">
                      <div class="col-12 d-flex align-items-center">
                        <label for="amount" class="me-2">Amount</label>
                        <span class="input-group-text">$</span>
                        <input type="text" id="amount" class="form-control ms-2" v-model="amount" @input="validateAmount" />
                      </div>
                      <div class="col-12 mt-2">
                        <label for="description">Description</label>
                        <textarea id="description" class="form-control" rows="10" v-model="description"></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Assignment Card -->
              <div class="col-xl-6 col-12 d-flex">
                <div class="card flex-grow-1">
                  <div class="card-body">
                    <h5 class="section-title">Assignment</h5>
                    <div class="row d-flex">
                      <div class="col-xxl-3 col-md-12 mt-4 align-items-start p-0">
                        <div class="checkbox checkbox-primary mb-2 d-flex align-items-center">
                          <input id="driver-checkbox" type="checkbox" class="form-check-input" disabled v-model="isDriverChecked">
                          <label for="driver-checkbox" class="form-check-label">Driver</label>
                        </div>
                      </div>
                      <div class="col-xxl-3 col-12">
                        <label for="driver-id">Driver ID</label>
                        <input type="text" id="driver-id" class="form-control" :disabled="!isDriverChecked" @keyup.enter="fetchDriverInfo" v-model="driverId" @input="handleDriverIdChange" data-bs-toggle="tooltip" title="Please press Enter after entering ID" />
                      </div>
                      <div class="col-xxl-6 col-12">
                        <label for="driver-name">Name</label>
                        <input type="text" id="driver-name" class="form-control" :disabled="true" :value="driverNameMessage" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- History Card -->
              <div class="col-xl-3 d-flex">
                <div class="card flex-grow-1">
                  <div class="card-body">
                    <h5 class="section-title">History</h5>
                    <div class="row">
                      <div class="col-12">
                        <label for="created">Created</label>
                        <input type="text" id="created" class="form-control" :value="created" disabled />
                      </div>
                      <div class="col-12 mt-2">
                        <label for="updated">Updated</label>
                        <input type="text" id="updated" class="form-control" :value="updated" disabled />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-green" @click="submitForm" :disabled="!canSubmit">OK</button>
            <button class="btn btn-cancel" @click="hideModal">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, defineProps, onMounted } from 'vue';
import { Modal, Tooltip } from 'bootstrap';
import Swal from 'sweetalert2';
import { useFeeAndRefundStore } from '@/store/admin/feeAndRefund';
import { useHttp } from '@/http/http';
import { statusCheck } from '@/store/public';

const store = useFeeAndRefundStore();

// 接收父組件傳入的 item
const props = defineProps({
  item: Object
});

const amount = ref(props.item.amount);
const description = ref(props.item.description);
const driverId = ref(props.item.driver_id || '');
const driverName = ref('');
const selectedSubgroup = ref(props.item.subgroup_id || '');
const created = ref(props.item.created_at);
const updated = ref(props.item.updated_at);
const isDriverChecked = ref(!!props.item.driver_id);
const isSubgroupChecked = ref(!!props.item.subgroup_id);
const validAmount = ref(true);
const driverInfoFetched = ref(!!props.item.driver_id);

const chargeModal = ref(null);
let modalInstance = null;

const showModal = () => {
  if (!modalInstance) {
    modalInstance = new Modal(chargeModal.value);
  }
  modalInstance.show();

  // 在模态打开时，如果有 driverId，自动进行搜索
  if (driverId.value) {
    fetchDriverInfo();
  }
};

const hideModal = () => {
  modalInstance.hide();
};

const driverNameMessage = computed(() => {
  if (driverId.value && !driverName.value) {
    return 'Name is not found';
  }
  return driverName.value;
});

const canSubmit = computed(() => {
  return validAmount.value && driverInfoFetched.value && amount.value && description.value;
});

const handleDriverIdChange = () => {
  driverInfoFetched.value = false;
  driverName.value = '';
};

// 提交表單
const submitForm = async () => {
  const payload = {
    id: props.item.id,
    type: store.currentType,
    amount: amount.value,
    description: description.value,
    driver_id: isDriverChecked.value ? driverId.value : '',
    subgroup_id: isSubgroupChecked.value ? selectedSubgroup.value : ''
  };

  await store.updateFeeAndRefund(payload);
  hideModal();
};

// 根據 driverId 獲取 driver 信息
const fetchDriverInfo = async () => {
  try {
    const params = {
      per_page: 1,
      page: 1,
      driver_id: driverId.value,
    };

    const response = await useHttp().post('/api/admin/driver/list', params);
    if (statusCheck(response.data)) {
      const drivers = response.data.drivers;
      if (drivers.length > 0) {
        driverName.value = drivers[0].first_name + ' ' + drivers[0].middle_name + ' ' + drivers[0].last_name;
        driverInfoFetched.value = true;
      } else {
        driverName.value = '';
        driverInfoFetched.value = false;
      }
    } else {
      driverName.value = '';
      driverInfoFetched.value = false;
      console.error('Failed to fetch driver');
    }
  } catch (error) {
    driverName.value = '';
    driverInfoFetched.value = false;
    console.error('Error fetching driver:', error);
  }
};

// 驗證金額
const validateAmount = (event) => {
  let value = event.target.value;

  // 儲存游標當前的位置
  const cursorPosition = event.target.selectionStart;

  // 移除非數字字符，並確保不是負數
  value = value.replace(/[^0-9.]/g, ''); // 僅保留數字和小數點
  if (value.startsWith('-')) {
    value = ''; // 禁止負數
  }

  // 檢查總位數是否符合要求
  const regex = /^\d{0,8}(\.\d{0,2})?$/;
  if (regex.test(value)) {
    validAmount.value = true;
    amount.value = value;
  } else {
    validAmount.value = false;
    Swal.fire({
      icon: 'error',
      title: 'Invalid Amount Format',
      text: 'Amount must be a valid number with up to 8 digits before and 2 digits after the decimal point.',
    });
    amount.value = '';
  }

  // 重新設置輸入框中的值並保持游標位置
  setTimeout(() => {
    event.target.value = value;
    event.target.setSelectionRange(cursorPosition, cursorPosition);
  });
};

// Tooltip 初始化
onMounted(() => {
  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  tooltipTriggerList.forEach(tooltipTriggerEl => {
    new Tooltip(tooltipTriggerEl);
  });

   // 在模态打开时，如果有 driverId，自动进行搜索
   if (driverId.value) {
    fetchDriverInfo();
  }
});
</script>



<style scoped>
.btn-green {
  background: rgba(0, 201, 98, 1);
  color: black;
  border: none;
  width: 150px;
  height: 46px;
  border-radius: 30px;
  padding: 10px, 32px, 10px, 32px;
}

.btn-green:hover {
  background-color: #00b452;
}

.btn-cancel {
  /* BTN_B */

    box-sizing: border-box;

    /* Auto layout */
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 14px 32px;
    gap: 10px;

    width: 150px;
    height: 46px;

    /* Indigo */
    border: 2px solid #5248DA;
    border-radius: 30px;

    /* Inside auto layout */
    flex: none;
    order: 1;
    flex-grow: 0;
}

.btn-cancel:hover {
  border: 2px solid #5248DA;
}

.modal-content {
  border-radius: 10px;
  padding: 20px;
}

.modal-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-footer {
  border-top: 1px solid #dee2e6;
  background-color: #f8f9fa;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

.card {
  background-color: #d9dde3;
  border-radius: 10px;
}

.section-title {
  font-size: 1.2rem;
  margin-bottom: 15px;
}

.form-control, .form-select {
  border-radius: 5px;
  padding: 10px;
  border: 1px solid #ced4da;
}

.close {
  color: #333;
  opacity: 1;
  font-size: 20px;
  padding: 0;
  background: none;
  border: none;
}

.btn-edit {
  color: #8476f5;
}

.close:hover {
  color: #000;
}

.row.d-flex {
  display: flex;
}

.card.flex-grow-1 {
  flex-grow: 1;
}

.modal-dialog.custom-modal {
  max-width: 80%;
  width: 80%;
  max-height: 90vh;
}

.modal-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.modal-body {
  flex-grow: 1;
  overflow-y: auto;
}

.modal-header, .modal-footer {
  flex-shrink: 0;
}

.checkbox-primary .form-check-input {
  width: 35px;
  height: 35px;
  border: 2px solid #8476f4;
  border-radius: 5px;
  background-color: white;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
  position: relative;
  vertical-align: middle;
}

.checkbox-primary .form-check-input:checked {
  background-color: #8476f4;
  border-color: #8476f4;
}

.checkbox-primary .form-check-input:checked::before {
  content: '✓';
  color: white;
  font-weight: bold;
  font-size: 18px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -60%);
}

.checkbox-primary .form-check-label {
  font-size: 18px;
  margin-left: 10px;
  vertical-align: middle;
}

@media (max-width: 768px) {
  .modal-dialog.custom-modal {
    max-width: 100%;
    width: 100%;
    height: 100vh;
    margin: 0;
  }

  .modal-content {
    height: 100%;
    border-radius: 0;
  }

  .modal-body {
    overflow-y: auto;
  }
}
</style>
